var _sentryCollisionFreeGlobalObject = typeof window != "undefined" ? window : typeof global != "undefined" ? global : typeof self != "undefined" ? self : {};
_sentryCollisionFreeGlobalObject["__sentryRewritesTunnelPath__"] = undefined;
_sentryCollisionFreeGlobalObject["SENTRY_RELEASE"] = {"id":"0465aa3d"};
_sentryCollisionFreeGlobalObject["__sentryBasePath"] = undefined;
_sentryCollisionFreeGlobalObject["__rewriteFramesAssetPrefixPath__"] = "";

// This file configures the initialization of Sentry on the client.
// The added config here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

import * as Sentry from '@sentry/nextjs'
import axios from 'axios'
import Cookies from 'js-cookie'

const isRemyPage = () => /^\/(?:3dgs\/remy(?:\/|$)|3dspace\/(?:detail|demo)(?:\/|$))/.test(window.location.pathname)

// 3DGS 的模型/相机/体素/视频等 output 文件通常体积较大，浏览器 tracing 会将
// 其自动归类为 “Large HTTP payload”。只按文件后缀识别，避免依赖 CDN 的路径
// 结构；仅过滤 3DGS transaction 中命中的 output span，保留其他网络数据。
const is3dgsOutputResource = (span) => {
  const value = [
    span?.description,
    span?.data?.url,
    span?.data?.['http.url'],
    span?.tags?.url,
  ].filter(Boolean).join(' ')
  return /\.(?:sog|ply|json|zip|mp4)(?:[?#]|$)/i.test(value)
}

const is3dgsTransaction = (event) => {
  const transaction = event?.transaction || event?.contexts?.trace?.description || ''
  return /\/(?:3dgs\/remy|3dspace\/(?:detail|demo))(?:\/|$)/.test(transaction)
}

function getLCP() {
  return new Promise((resolve) => {
    const observer = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      // LCP 条目可能会更新（比如更大的内容加载），取最新的
      const lcpEntry = entries[entries.length - 1];
      const lcpTime = lcpEntry.renderTime || lcpEntry.loadTime;
      resolve(lcpTime);
    });

    try {
      // 监听 largest-contentful-paint 类型
      observer.observe({
        type: 'largest-contentful-paint',
        buffered: true
      });
    } catch (e) {
      console.error('监听 LCP 失败:', e);
      resolve(null);
    }

    // 页面隐藏时确定 LCP（避免页面一直不卸载导致无法返回）
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        observer.disconnect();
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
  })
}

Sentry.init({
  dsn: window.SENTRY_DSN,

  environment: window.SENTRY_ENVIRONMENT,

  enableTracing: true,

  enabled: true,

  // Error events are retained for Remy and sampled at 100%.
  sampleRate: 1,

  denyUrls: [
    'https://f8ds5tkidk-sdk.rum.aliyuncs.com/v2/browser-sdk.js',
    'https://g.alicdn.com/AWSC/AWSC/awsc.js',
    'https://o.alicdn.com/captcha-frontend/aliyunCaptcha/AliyunCaptcha.js',
    'https://g.alicdn.com/AWSC/nc/1.97.0/nc.js',
    'https://snssdk.insta360.com/minio.byterangers.onpremise.docor.static/collect-privity-v5.1.12.js',
    'https://res.insta360.com/static/assets/storage/20200714/910b039c4115f9f54d62321023b1d0f3/thinkingdata.min.js',
  ],

  // Adds request headers and IP for users, for more info visit:
  // https://docs.sentry.io/platforms/javascript/guides/nextjs/configuration/options/#sendDefaultPii
  sendDefaultPii: true,

  initialScope: {
    tags: {
      'x-language': window.locale,
      'insta:service': 'fe-app',
      'insta:app-version': Cookies.get('insta_app_version'),
    },
  },

  // Add optional integrations for additional features
  integrations: [
    ...(isRemyPage()
      ? [
          Sentry.browserApiErrorsIntegration({
            requestAnimationFrame: false,
          }),
        ]
      : []),
    Sentry.browserTracingIntegration({
      enableInp: true,
    }),
    Sentry.replayIntegration(),
  ],

  // Define how likely traces are sampled. Adjust this value in production, or use tracesSampler for greater control.
  tracesSampleRate: 1,

  normalizeDepth: 12,

  release: window.NEXT_BUILD_ID,

  // Setting this option to true will print useful information to the console while you're setting up Sentry.
  debug: false,

  // 这些是 React 18 hydration / suspense 内部错误码，已被业务感知与处理，
  // 单独成 issue 反而把 breadcrumbs 刷满，影响真正异常的现场还原
  ignoreErrors: [
    /Minified React error #(418|419|421|422|423|425)/,
  ],
  beforeBreadcrumb(crumb) {
    if (crumb?.category === 'sentry.event' && /Minified React error #(418|419|421|422|423|425)/.test(crumb?.message || '')) return null
    return crumb
  },

  beforeSend(event, hint) {
    try {
      const { isAxiosError, isRequestAborted, isTimeout, name, message } = hint.originalException
      // Check for webview invoke error
      if (message?.startsWith('[webview_invoke_app]')) {
        console.debug('Webview invoke error')
        return null // webview invoke error
      }
      // Check for Axios cancellation
      if (axios.isCancel(hint?.originalException)) {
        console.debug('Cancelled Axios request ignored by Sentry')
        return null // Return null to discard the event
      }
      // Check for generic AbortError (e.g., from Fetch API)
      if (name === 'AbortError' || message?.startsWith('AbortError') || (isAxiosError && message?.startsWith('Request aborted')) || isRequestAborted) {
        console.debug('Aborted Fetch request ignored by Sentry')
        return null // Return null to discard the event
      }
      if (name === 'NotAllowedError' || message?.startsWith('NotAllowedError')) {
        return null
      }
      // Check for axios Network Error
      if (isAxiosError && message === 'Network Error') {
        console.debug('Axios Fetch request network error ignored by Sentry')
        return null // Return null to discard the event
      }
      if (isAxiosError && isTimeout) {
        event.tags['insta:mute'] = true
      }
      // 火山 SDK 内部调用错误，不影响业务
      if (
        message === `undefined is not an object (evaluating 'window.webkit.messageHandlers.TEANativeReport.postMessage')` ||
        message === `undefined is not an object (evaluating 'window.webkit.messageHandlers')`
      ) {
        return null
      }
      if (name === 'ReferenceError' && message === `Can't find variable: zaloJSV2`) {
        return null
      }
      return event
    } catch { }
  },

  async beforeSendTransaction(event) {
    try {
      if (is3dgsTransaction(event) && Array.isArray(event.spans)) {
        event.spans = event.spans.filter((span) => !is3dgsOutputResource(span))
      }
      const lcpTime = await getLCP()
      if (lcpTime) {
        event.measurements.lcp = { value: lcpTime, unit: 'millisecond' }
      }
    } catch (error) {
      console.error('send performance event beforeSendTransaction error', error)
    }
    return event
  },
})

window.addEventListener('unhandledrejection', (event) => { })
